import React, { useState } from "react";
import { Alert, Box, Button, Typography } from "@mui/material";
import AhaStatusChip from "./AhaStatusChip";
import AhaResolveDialog from "./AhaResolveDialog";
import { recheckAhaIssuance } from "../../api/apiClient";
import { STUDENT_BLOCKER_TEXT, blockerText, errorCodeText } from "./ahaText";

const MONTHS = ["Jan", "Feb", "Mar", "Apr", "May", "Jun", "Jul", "Aug", "Sep", "Oct", "Nov", "Dec"];

/** "2026-09-21" -> "Sep 21" (or "Sep 21, 2026"). Parsed by hand so the date never shifts with the time zone. */
function shortDate(isoDate, withYear) {
  const [year, month, day] = String(isoDate || "").split("-").map(Number);
  if (!year || !month || !day || !MONTHS[month - 1]) return null;
  return `${MONTHS[month - 1]} ${day}${withYear ? `, ${year}` : ""}`;
}

function CheckIcon({ size = 10 }) {
  return (
    <svg width={size} height={size} viewBox="0 0 24 24" fill="none" stroke="currentColor" strokeWidth="3.5"
      strokeLinecap="round" strokeLinejoin="round" aria-hidden="true">
      <path d="M20 6 9 17l-5-5" />
    </svg>
  );
}

const PILL = {
  display: "inline-flex",
  alignItems: "center",
  gap: "6px",
  fontWeight: 700,
  fontSize: "12.5px",
  whiteSpace: "nowrap",
  borderRadius: "999px",
  py: "4px",
  pl: "6px",
  pr: "10px",
};

const PILL_ICON = {
  width: 16,
  height: 16,
  borderRadius: "50%",
  display: "grid",
  placeItems: "center",
  flexShrink: 0,
};

const SUB = { fontSize: "11.5px", color: "var(--color-accent-violet-mid)", whiteSpace: "nowrap" };

function ReadyCell({ item }) {
  const signed = shortDate(item.completionDate, false);
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", alignItems: "flex-start" }}>
      <Box sx={{ ...PILL, bgcolor: "var(--color-success-bg)", color: "var(--color-success)" }}>
        <Box sx={{ ...PILL_ICON, bgcolor: "var(--color-success)", color: "#fff" }}><CheckIcon size={9} /></Box>
        Passed · Ready for AHA
      </Box>
      <Box sx={{ display: "flex", gap: "10px", ...SUB, "& svg": { color: "var(--color-success)" } }}>
        <Box component="span" sx={{ display: "flex", gap: "4px", alignItems: "center" }}>
          <CheckIcon />Requirements met
        </Box>
        {signed && (
          <Box component="span" sx={{ display: "flex", gap: "4px", alignItems: "center" }}>
            <CheckIcon />{`Signed off ${signed}`}
          </Box>
        )}
      </Box>
    </Box>
  );
}

function IssuedCell({ item }) {
  const completed = shortDate(item.completionDate, true);
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", alignItems: "flex-start" }}>
      <Box sx={{ ...PILL, bgcolor: "var(--color-primary)", color: "var(--color-on-primary, #fff)" }}>
        <Box sx={{ ...PILL_ICON, bgcolor: "var(--color-lime)", color: "var(--color-primary)" }}><CheckIcon size={9} /></Box>
        Card issued
      </Box>
      {completed && <Typography component="span" sx={SUB}>{`Completed ${completed}`}</Typography>}
    </Box>
  );
}

function NotReadyCell({ item }) {
  const reasons = (item.blockers || []).map((b) => STUDENT_BLOCKER_TEXT[b] || blockerText(b));
  if (!reasons.length) return <Typography component="span" sx={{ fontSize: 13, color: "var(--color-hairline-cool, #9a95a8)" }}>—</Typography>;
  // Every reason is visible text (not a hover-only tooltip) so keyboard and screen-reader users get it too.
  return <Typography component="span" sx={{ ...SUB, whiteSpace: "normal" }}>{reasons.join(" · ")}</Typography>;
}

/** "45 min", "3 hours", "2 days" for an age in seconds. */
export function formatWaited(seconds) {
  if (seconds == null) return null;
  if (seconds < 5400) return `${Math.max(1, Math.round(seconds / 60))} min`;
  if (seconds < 172800) return `${Math.round(seconds / 3600)} hours`;
  return `${Math.round(seconds / 86400)} days`;
}

/**
 * A card request stuck queued or sending: how long it has waited, what that means, and for staff the next step.
 * A stuck send may already have reached AHA, so it is never simply re-sent: it moves to "result unknown", staff check
 * AHA's records, and only when AHA has no card is it sent again. A stuck queued request had not left yet, so it is
 * sent again straight away.
 */
function StuckNotice({ item, classId, canResolve, onChecked }) {
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState("");
  const sending = item.issuanceState === "SENDING";
  const waited = formatWaited(item.issuanceAgeSeconds);

  const check = async (e) => {
    e.stopPropagation();
    setBusy(true);
    setError("");
    try {
      const outcome = await recheckAhaIssuance(classId, item.registrationId);
      onChecked(outcome);
    } catch (err) {
      setError(err?.response?.data?.message || "The request could not be checked. Nothing was changed.");
    } finally {
      setBusy(false);
    }
  };

  return (
    <Box data-testid="aha-stuck-notice" sx={{ display: "flex", flexDirection: "column", gap: "6px", alignItems: "flex-start", maxWidth: 280 }}>
      <Typography component="span" sx={{ ...SUB, whiteSpace: "normal" }}>
        {sending
          ? `Sending started ${waited || "a while"} ago and never finished. AHA may or may not have received it.`
          : `Queued ${waited || "a while"} ago and not picked up by the sender.`}
      </Typography>
      {canResolve ? (
        <>
          <Typography component="span" sx={{ ...SUB, whiteSpace: "normal" }}>
            {sending
              ? "To resend: check it here, look the student up on AHA's eCards site, then record the card AHA shows or that AHA has none. Only then can it be sent again, so the student never gets two cards."
              : "Nothing reached AHA yet, so it can be sent again now."}
          </Typography>
          <Button size="small" variant="outlined" color="warning" onClick={check} disabled={busy}
            sx={{ textTransform: "none", fontWeight: 600, py: 0.25 }}>
            {busy ? "Checking…" : sending ? "Check and resend" : "Send again now"}
          </Button>
        </>
      ) : (
        <Typography component="span" sx={{ ...SUB, whiteSpace: "normal" }}>Ask a coordinator to check and resend it.</Typography>
      )}
      {error && <Alert severity="error" sx={{ py: 0, fontSize: 12 }}>{error}</Alert>}
    </Box>
  );
}

/** Queued, sending, rejected or unknown: the state, why (when known), and for staff a way out of UNKNOWN. */
function RequestCell({ item, classId, canResolve, onResolved }) {
  const [resolving, setResolving] = useState(false);
  const reason = errorCodeText(item.errorCode);
  const stuck = item.issuanceStuck && ["PENDING", "SENDING"].includes(item.issuanceState);
  // A stuck send that becomes "result unknown" goes straight on to the resolve step, the way to send it again.
  const afterCheck = (outcome) => {
    if (outcome?.state === "UNKNOWN") setResolving(true);
    if (onResolved) onResolved(outcome);
  };
  return (
    <Box sx={{ display: "flex", flexDirection: "column", gap: "6px", alignItems: "flex-start" }}>
      <AhaStatusChip item={item} />
      {stuck && <StuckNotice item={item} classId={classId} canResolve={canResolve} onChecked={afterCheck} />}
      {reason && <Typography component="span" sx={{ ...SUB, whiteSpace: "normal" }}>{reason}</Typography>}
      {item.issuanceState === "REJECTED" && item.eligible && (
        <Typography component="span" sx={{ ...SUB, whiteSpace: "normal" }}>Can be selected and sent again.</Typography>
      )}
      {item.issuanceState === "UNKNOWN" && canResolve && (
        <>
          <Typography component="span" sx={{ ...SUB, whiteSpace: "normal" }}>
            Check AHA&apos;s records, then resolve. If AHA has no card, the student can be sent again.
          </Typography>
          <Button size="small" variant="outlined" onClick={(e) => { e.stopPropagation(); setResolving(true); }}
            sx={{ textTransform: "none", fontWeight: 600, py: 0.25 }}>
            Resolve
          </Button>
        </>
      )}
      {/* One dialog for both: a stuck send opens it right after it is checked, and keeps it open as it reloads as UNKNOWN. */}
      {canResolve && (item.issuanceState === "UNKNOWN" || stuck) && (
        <AhaResolveDialog open={resolving} classId={classId} student={item} onClose={() => setResolving(false)}
          onResolved={onResolved} />
      )}
    </Box>
  );
}

/**
 * The roster's AHA eCard column for one student: a clear "ready" state once they have passed and been signed off,
 * a "card issued" state after AHA assigns the card, and the request/blocker state otherwise.
 */
export default function AhaRosterCell({ item, classId, canResolve = false, onResolved }) {
  if (!item) return <Typography component="span" sx={{ fontSize: 13, color: "var(--color-hairline-cool, #9a95a8)" }}>—</Typography>;
  if (item.issuanceState === "ASSIGNED") return <IssuedCell item={item} />;
  if (item.issuanceState) {
    return <RequestCell item={item} classId={classId} canResolve={canResolve} onResolved={onResolved} />;
  }
  if (item.eligible) return <ReadyCell item={item} />;
  return <NotReadyCell item={item} />;
}
